import "./transaction-child-first.scss";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Button } from "primereact/button";

import FormMemberWrapper from "@domain/member/form-member-wrapper/form-member-wrapper";
import ChildInfo from "@core/ui/child-info/child-info";
import { LabelValue } from "@shared/models/label-value.interface";
import { MemberFormSchema } from "@factory/form/form.rule";
import { useTransactionStepContext } from "../transaction-step.context";
import { useNewTransactionContext } from "../new-transaction.context";
import { viewAsset } from "@factory/query/google-cloud-storage.query";

const getAvatarCache = (): Map<string, string> => {
  const win = window as any;
  if (!win.__avatarCache) {
    win.__avatarCache = new Map<string, string>();
  }
  return win.__avatarCache;
};

export function TransactionChildFirst() {
  const {
    childFirstList,
    childFormCount,
    childFormRefs,
    handleAddChildForm,
    selectedChildren,
    setSelectedChildren,
    handleChildFormSubmit,
    handleChildTrigger,
    nextStepper,
    isCreateNewChild,
    setIsCreateNewChild,
    isChildFirstListLoading,
    childFormData,
    childFormKeys,
    handleRemoveChildForm,
  } = useTransactionStepContext();
  const {
    isCreating,
  } = useNewTransactionContext();

  const [isSavingNewFlow, setIsSavingNewFlow] = useState(false);
  const [childDrafts, setChildDrafts] = useState<MemberFormSchema[]>(() => childFormData);
  const childDraftsRef = useRef<MemberFormSchema[]>(childFormData);
  const saveLockRef = useRef(false);

  const selectedChildrenData = useMemo(() => {
    return selectedChildren
      .map((code) => childFirstList.find((item) => item.value === code))
      .filter(Boolean) as LabelValue[];
  }, [childFirstList, selectedChildren]);

  const [resolvedPhotos, setResolvedPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const cache = getAvatarCache();

    // Instantly resolve cached avatar blob URLs to prevent stale photos lingering or delayed loads.
    const initialPhotos: Record<string, string> = {};
    selectedChildrenData.forEach((child) => {
      const avatar = child.payload?.avatar;
      if (avatar && cache.has(avatar)) {
        initialPhotos[child.value] = cache.get(avatar)!;
      }
    });
    setResolvedPhotos(initialPhotos);

    const fetchPhotos = async () => {
      const promises = selectedChildrenData.map(async (child) => {
        const avatar = child.payload?.avatar;
        if (!avatar) return { key: child.value, url: "" };

        if (cache.has(avatar)) {
          return { key: child.value, url: cache.get(avatar)! };
        }
        try {
          const elementFile = await viewAsset(avatar);
          const fileBlob = URL.createObjectURL(elementFile);
          cache.set(avatar, fileBlob);
          return { key: child.value, url: fileBlob };
        } catch (err) {
          console.error("Failed to load avatar", err);
          return { key: child.value, url: "" };
        }
      });

      const resolved = await Promise.all(promises);
      if (active) {
        const newResolved: Record<string, string> = {};
        resolved.forEach((item) => {
          if (item.url) {
            newResolved[item.key] = item.url;
          }
        });
        setResolvedPhotos(newResolved);
      }
    };

    fetchPhotos();
    return () => {
      active = false;
    };
  }, [selectedChildrenData]);

  // Sync drafts when a form is removed (i.e. length of childFormData changes)
  useEffect(() => {
    if (childFormData.length !== childDraftsRef.current.length) {
      childDraftsRef.current = childFormData;
      setChildDrafts(childFormData);
    }
  }, [childFormData]);

  const handleRemoveChild = (index: number) => {
    const updatedDrafts = [...childDraftsRef.current];
    updatedDrafts.splice(index, 1);
    childDraftsRef.current = updatedDrafts;
    setChildDrafts(updatedDrafts);
    handleRemoveChildForm(index);
  };

  const handleSelectChildren = useCallback(
    (e: MultiSelectChangeEvent) => {
      setSelectedChildren(e.value || []);
    },
    [setSelectedChildren]
  );

  const getSubmittedChildren = useCallback(
    (children: MemberFormSchema[]) => {
      return Array.from({ length: childFormCount }, (_, index) => children[index]).filter(
        Boolean
      ) as MemberFormSchema[];
    },
    [childFormCount]
  );

  const handleCreateChild = () => {
    setIsCreateNewChild(true);
    setSelectedChildren([]);
  };
  const handleSelectChildMode = () => {
    setIsCreateNewChild(false);
    setIsSavingNewFlow(false);
    saveLockRef.current = false;
  };
  const handleAddAnotherChild = () => {
    handleAddChildForm();
  };
  const handleChildSubmit = (data: MemberFormSchema, index: number) => {
    const updatedForms = [...childDraftsRef.current];
    updatedForms[index] = data;
    childDraftsRef.current = updatedForms;
    setChildDrafts(updatedForms);
    handleChildFormSubmit(data, index);
  };
  const handleChildChange = (data: MemberFormSchema, index: number) => {
    const updatedForms = [...childDraftsRef.current];
    updatedForms[index] = data;
    childDraftsRef.current = updatedForms;
    setChildDrafts(updatedForms);
    handleChildFormSubmit(data, index);
  };

  const handleChildFirstNext = () => {
    if (!isCreateNewChild) {
      if (selectedChildren.length > 0) {
        nextStepper();
      }
      return;
    }
  };
  const handleSaveNewFlow = async () => {
    if (saveLockRef.current) {
      return;
    }

    saveLockRef.current = true;
    setIsSavingNewFlow(true);

    handleChildTrigger();

    // Allow form submit callbacks to populate draft refs before saving.
    await new Promise((resolve) => setTimeout(resolve, 0));

    const childrenForms = getSubmittedChildren(childDraftsRef.current);
    if (childrenForms.length !== childFormCount) {
      setIsSavingNewFlow(false);
      saveLockRef.current = false;
      return;
    }

    setIsSavingNewFlow(false);
    saveLockRef.current = false;
    nextStepper();
  };

  const isNextDisabled = isCreating || (!isCreateNewChild && selectedChildren.length === 0);

  return (
    <div id="TransactionChildFirst" className="transaction-child-first flex flex-col items-center">
      <div className="w-full md:w-8/12 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          {!isCreateNewChild ? (
            <section className="flex flex-col gap-3">
              <div>
                <MultiSelect
                  value={selectedChildren}
                  onChange={handleSelectChildren}
                  options={childFirstList}
                  placeholder={isChildFirstListLoading ? "Loading children list..." : "Select child"}
                  filter
                  filterPlaceholder="Search child name here..."
                  className="w-full"
                  virtualScrollerOptions={{ itemSize: 38 }}
                  disabled={isChildFirstListLoading}
                  loading={isChildFirstListLoading}
                />
              </div>
              {selectedChildrenData.length > 0 ? (
                <div className="mt-3 flex flex-col gap-3">
                  <small className="block font-semibold text-gray-700">Selected Children</small>
                  <div className="flex flex-wrap gap-4">
                    {selectedChildrenData.map((child) => (
                      <ChildInfo
                        key={child.value}
                        name={child.label || ""}
                        birthday={child.payload?.birthday}
                        photo={resolvedPhotos[child.value]}
                        onRemove={() => {
                          setSelectedChildren((prev) => prev.filter((code) => code !== child.value));
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : (
            <section className="flex flex-col gap-4">
              {childFormKeys.map((formKey, index) => (
                <div className="border border-dashed p-4 rounded-lg bg-gray-50/50 shadow-sm relative" key={formKey}>
                  <div className="flex justify-between items-center mb-2">
                    <small className="block font-semibold text-gray-700">
                      Create child #{index + 1}
                    </small>
                    {childFormCount > 1 && (
                      <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-rounded p-button-text p-button-danger"
                        style={{ width: '2rem', height: '2rem' }}
                        onClick={() => handleRemoveChild(index)}
                        tooltip="Remove child form"
                        tooltipOptions={{ position: 'left' }}
                      />
                    )}
                  </div>
                  <FormMemberWrapper
                    ref={(el) => {
                      childFormRefs.current[index] = el!;
                    }}
                    isChild={true}
                    defaultValues={childDrafts[index]}
                    onChange={(data) => handleChildChange(data, index)}
                    onSubmit={(data) => handleChildSubmit(data, index)}
                  />
                </div>
              ))}
              <div className="flex justify-center mt-2">
                <Button
                  type="button"
                  icon="pi pi-plus"
                  rounded
                  outlined
                  onClick={handleAddAnotherChild}
                  className="p-button-lg shadow-sm hover:bg-gray-100 transition-all"
                  style={{ width: '3rem', height: '3rem', borderRadius: '50%' }}
                  tooltip="Add another child"
                  tooltipOptions={{ position: 'bottom' }}
                />
              </div>
            </section>
          )}
        </div>

        {!isCreateNewChild ? (
          <div className="flex pt-4 justify-end gap-2 border-t mt-2">
            <Button
              label="Create Child"
              icon="pi pi-plus"
              iconPos="right"
              outlined
              severity="secondary"
              onClick={handleCreateChild}
            />
            <Button
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              disabled={isNextDisabled}
              onClick={handleChildFirstNext}
            />
          </div>
        ) : (
          <div className="flex pt-4 justify-between border-t mt-2">
            <Button
              label="Select Child"
              severity="secondary"
              outlined
              onClick={handleSelectChildMode}
            />
            <Button
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              disabled={isCreating || isSavingNewFlow}
              onClick={handleSaveNewFlow}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionChildFirst;
